import { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useMap } from "react-leaflet";
import L, { latLngBounds } from "leaflet";
import type { Container } from "@/types/container";
import { useResizeObserver } from "@/hooks/use-resize-observer";
import { ContainerGlassDetail } from "@/components/application/map/container-glass-detail";
import { LIST_PANEL_WIDTH_PX, POPUP_OFFSET_PX, POPUP_MAX_WIDTH_PX } from "./world-map-utils";
import type { WorldMapRoute } from "./world-map-utils";

export function FitBounds({
    points,
    selectedContainerId,
    routes,
    isPanelOpen,
}: {
    points: [number, number][];
    selectedContainerId?: string | null;
    routes?: WorldMapRoute[];
    isPanelOpen?: boolean;
}) {
    const map = useMap();

    const targetPoints = useMemo(() => {
        if (selectedContainerId && routes) {
            const selectedRoute = routes.find(route => route.c.id === selectedContainerId);
            if (selectedRoute) {
                return selectedRoute.coordinates;
            }
        }
        return points;
    }, [selectedContainerId, routes, points]);

    useEffect(() => {
        if (!targetPoints.length) return;
        const timer = setTimeout(() => {
            const bounds = latLngBounds(targetPoints);
            const fitOptions: L.FitBoundsOptions = {
                maxZoom: 4,
            };
            if (isPanelOpen) {
                fitOptions.paddingTopLeft = [LIST_PANEL_WIDTH_PX, 40];
                fitOptions.paddingBottomRight = [40, 40];
            } else {
                fitOptions.padding = [40, 40];
            }
            map.fitBounds(bounds, fitOptions);
        }, 100);
        return () => clearTimeout(timer);
    }, [map, JSON.stringify(targetPoints), selectedContainerId, isPanelOpen]);
    return null;
}

export function MapResizeHandler({ wrapperRef, isPanelOpen }: { wrapperRef: React.RefObject<HTMLDivElement | null>; isPanelOpen: boolean }) {
    const map = useMap();

    useResizeObserver({
        ref: wrapperRef,
        onResize: () => {
            setTimeout(() => {
                map.invalidateSize();
            }, 0);
        },
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            map.invalidateSize();
            if (map.getCenter) {
                const currentCenter = map.getCenter();
                map.setView(currentCenter, map.getZoom());
            }
        }, 350);
        return () => clearTimeout(timer);
    }, [map, isPanelOpen]);

    useEffect(() => {
        const timers = [
            setTimeout(() => map.invalidateSize(), 0),
            setTimeout(() => map.invalidateSize(), 100),
            setTimeout(() => map.invalidateSize(), 300),
        ];
        return () => {
            timers.forEach(timer => clearTimeout(timer));
        };
    }, [map]);

    return null;
}

export function GlassDetailOverlay({
    selectedContainer,
    selectedMarkerLatLng,
    lastUpdateTime,
    formatDateTime,
    isOpen,
    activeIssuesCount,
    onClose,
    onCollapse,
    collapseTooltip,
}: {
    selectedContainer: Container | null;
    selectedMarkerLatLng: [number, number] | null;
    lastUpdateTime: Date | null;
    formatDateTime: (date: Date) => string;
    isOpen: boolean;
    /** Conteggio active issues dall'API (stesso della pagina dettaglio). Se fornito, ha precedenza sul container. */
    activeIssuesCount?: number;
    onClose: () => void;
    onCollapse: () => void;
    collapseTooltip: string;
}) {
    const map = useMap();
    const [pixelPosition, setPixelPosition] = useState<{ x: number; y: number } | null>(null);

    const updatePosition = useMemo(() => {
        return () => {
            if (!selectedMarkerLatLng) {
                setPixelPosition(null);
                return;
            }
            const container = map.getContainer();
            if (!container) return;
            const point = map.latLngToContainerPoint(selectedMarkerLatLng);
            const size = map.getSize();
            let offsetX = POPUP_OFFSET_PX;
            if (point.x + POPUP_MAX_WIDTH_PX > size.x) {
                offsetX = -POPUP_MAX_WIDTH_PX - POPUP_OFFSET_PX;
            }
            const rect = container.getBoundingClientRect();
            setPixelPosition({
                x: rect.left + point.x + offsetX,
                y: rect.top + point.y,
            });
        };
    }, [map, selectedMarkerLatLng]);

    useEffect(() => {
        if (!selectedContainer || !selectedMarkerLatLng) {
            setPixelPosition(null);
            return;
        }
        updatePosition();
        map.on('moveend', updatePosition);
        map.on('zoomend', updatePosition);
        return () => {
            map.off('moveend', updatePosition);
            map.off('zoomend', updatePosition);
        };
    }, [map, selectedContainer, selectedMarkerLatLng, updatePosition]);

    if (!isOpen || !selectedContainer || !selectedMarkerLatLng || !pixelPosition) return null;

    return createPortal(
        <div
            className="max-w-md w-full z-[1000] pointer-events-auto"
            style={{
                position: 'fixed',
                left: pixelPosition.x,
                top: pixelPosition.y,
                transform: 'translateY(-50%)',
            }}
        >
            <ContainerGlassDetail
                container={selectedContainer}
                lastUpdateTime={lastUpdateTime ?? undefined}
                formatDateTime={formatDateTime}
                activeIssuesCount={activeIssuesCount}
                onClose={onClose}
                onCollapse={onCollapse}
                collapseTooltip={collapseTooltip}
            />
        </div>,
        document.body
    );
}
